import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Alert, Button, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import { getIndividualResponseDetail, getIndividualResponses, IndividualAnswer } from '../../../api/customSurveys';
import { QuestionType } from '../../../api/customSurveyTemplates';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import classes from '../CustomSurveyResultsPage.module.css';

/** Nok til å bla mellom alle svarene i én undersøkelse */
const RESPONSE_LIMIT = 100;

type ResponseDetailProps = {
  templateId: string;
  surveyId: number;
  responseId: number;
  /** Siden i listen svaret ble åpnet fra, så «Alle svar» går tilbake dit */
  returnPage?: number;
};

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

const Answer = ({ answer }: { answer: IndividualAnswer }) => {
  const { t } = useTranslation();
  const isScale = answer.questionType === QuestionType.LIKERT_SCALE || answer.questionType === QuestionType.NPS_SCALE;

  if (isScale && answer.numericValue !== null && answer.numericValue !== undefined) {
    const min = answer.scaleMin ?? (answer.questionType === QuestionType.NPS_SCALE ? 0 : 1);
    const max = answer.scaleMax ?? (answer.questionType === QuestionType.NPS_SCALE ? 10 : 5);
    const percent = max > min ? ((answer.numericValue - min) / (max - min)) * 100 : 0;
    return (
      <div className={classes.answerScale}>
        <span className={classes.answerScaleValue}>
          <span className={classes.answerValue}>{answer.numericValue}</span>{' '}
          {t('customSurveys.templateTrends.likert.of', { max })}
        </span>
        <span aria-hidden="true" className={classes.answerTrack}>
          <span style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
        </span>
      </div>
    );
  }

  const choices =
    answer.questionType === QuestionType.YES_NO && answer.numericValue !== null && answer.numericValue !== undefined
      ? [answer.numericValue === 1 ? t('customSurveys.templateTrends.choice.yes') : t('customSurveys.templateTrends.choice.no')]
      : (answer.selectedOptions ?? []);

  if (choices.length > 0) {
    return (
      <ul className={classes.answerChoices}>
        {choices.map((choice) => (
          <li key={choice}>
            <Tag data-color="accent">{choice}</Tag>
          </li>
        ))}
      </ul>
    );
  }

  if (answer.textValue) {
    return <Paragraph className={classes.answerText}>{answer.textValue}</Paragraph>;
  }

  return (
    <Paragraph data-size="sm" className={classes.subtle}>
      {t('customSurveys.surveyResults.response.notAnswered')}
    </Paragraph>
  );
};

/** Ett svar med alle spørsmålene, og blaing mellom svarene */
export const ResponseDetail = ({ templateId, surveyId, responseId, returnPage }: ResponseDetailProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: list } = useQuery({
    queryKey: ['individualResponses', String(surveyId), 'all'],
    queryFn: () => getIndividualResponses(surveyId, RESPONSE_LIMIT, 0),
  });
  const { data: response, isLoading, error } = useQuery({
    queryKey: ['individualResponseDetail', String(surveyId), String(responseId)],
    queryFn: () => getIndividualResponseDetail(surveyId, responseId),
  });

  const surveyPath = buildRoute(Routes.DashboardCustomSurveySurvey, { templateId, surveyId });
  const responsePath = (id: number) =>
    buildRoute(Routes.DashboardCustomSurveyResponseDetail, { templateId, surveyId, responseId: id });
  const ids = list?.responses.map((item) => item.id) ?? [];
  const index = ids.indexOf(responseId);
  const back = (
    <Link to={surveyPath} state={{ returnPage: returnPage ?? 1 }} className="ds-link" data-size="sm">
      {t('customSurveys.surveyResults.response.allResponses')}
    </Link>
  );

  if (isLoading) {
    return <Skeleton variant="rectangle" width="100%" height={360} />;
  }

  if (error || !response) {
    const status = (error as AxiosError | null)?.response?.status;
    const key = status === 403 ? 'accessDenied' : status === 404 ? 'notFound' : 'error';
    return (
      <div className={classes.response}>
        {back}
        <Alert data-color={status === 403 ? 'warning' : status === 404 ? 'info' : 'danger'}>
          <Heading level={3} data-size="2xs">
            {t(`customSurveys.surveyResults.response.${key}.title`)}
          </Heading>
          <Paragraph data-size="sm">{t(`customSurveys.surveyResults.response.${key}.message`)}</Paragraph>
        </Alert>
      </div>
    );
  }

  const label =
    response.isAnonymous || !response.respondentEmail
      ? t('customSurveys.surveyResults.individual.response', { number: index >= 0 ? index + 1 : 1 })
      : response.respondentEmail;
  const submitted = new Date(response.submittedAt);
  const answers = [...response.answers].sort((a, b) => a.questionOrder - b.questionOrder);

  return (
    <section aria-labelledby="survey-response-heading" className={classes.response}>
      <div className={classes.responseIntro}>
        {back}
        <Heading level={2} data-size="sm" id="survey-response-heading">
          {label}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('customSurveys.surveyResults.response.submitted', {
            date: submitted.toLocaleDateString(locale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric' }),
            time: submitted.toLocaleTimeString(locale(i18n.language), { hour: '2-digit', minute: '2-digit' }),
          })}
        </Paragraph>
      </div>

      <ol className={classes.answerList}>
        {answers.map((answer, position) => (
          <li key={answer.questionId} className={classes.answerRow}>
            <span aria-hidden="true" className={classes.answerNumber}>
              {position + 1}
            </span>
            <div className={classes.answerBody}>
              <div className={classes.answerHeader}>
                <Heading level={3} data-size="2xs">
                  {answer.questionText}
                </Heading>
                <span className={classes.subtle}>
                  {t(`customSurveys.templateTrends.questionTypes.${answer.questionType}`, {
                    min: answer.scaleMin ?? 1,
                    max: answer.scaleMax ?? 5,
                    defaultValue: answer.questionType,
                  })}
                </span>
              </div>
              <Answer answer={answer} />
            </div>
          </li>
        ))}
      </ol>

      {ids.length > 1 && index >= 0 && (
        <nav aria-label={t('customSurveys.surveyResults.response.navigation')} className={classes.responseNav}>
          {/* Knappene skjules i stedet for å deaktiveres når det ikke finnes et svar å gå til */}
          {index > 0 ? (
            <Button variant="secondary" onClick={() => navigate(responsePath(ids[index - 1]), { state: { returnPage } })}>
              {t('customSurveys.surveyResults.response.previous')}
            </Button>
          ) : (
            <span />
          )}
          <span aria-live="polite" className={classes.subtle}>
            {t('customSurveys.surveyResults.response.position', { current: index + 1, total: ids.length })}
          </span>
          {index < ids.length - 1 ? (
            <Button variant="secondary" onClick={() => navigate(responsePath(ids[index + 1]), { state: { returnPage } })}>
              {t('customSurveys.surveyResults.response.next')}
            </Button>
          ) : (
            <span />
          )}
        </nav>
      )}
    </section>
  );
};
